import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useEffect, useState } from "react";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

export default function Readiness() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  // Mount-only fetch. `api` (module import) and setters are stable refs.
  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/readiness-score");
        setData(d);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Complete the assessment first.");
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]"><Nav />
        <div className="max-w-xl mx-auto px-6 py-16">
          <div className="overline">Not yet</div>
          <h1 className="font-serif-display text-4xl text-[#1E1B4B] mt-2">Your Readiness Score awaits.</h1>
          <p className="mt-4 text-stone-600">{err}</p>
          <Link to="/assessment" data-testid="readiness-take-assessment"
            className="btn-pill inline-flex mt-8 text-white" style={{ background: "#1E1B4B" }}>Take the Assessment</Link>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen"><Nav /><div className="p-12 text-stone-500">Reading the stars…</div></div>;

  const chart = [{ name: "score", value: data.overall, fill: "#D97706" }];

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="overline">Marriage Readiness Score <Sparkles className="inline text-amber-700" size={12} /></div>
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1E1B4B] mt-2">You, before the match.</h1>
        <p className="mt-3 text-stone-600 max-w-2xl">This is not a compatibility score — it&apos;s a mirror. It reflects how ready you are to receive a partner well.</p>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
            className="border border-stone-200 rounded-2xl bg-white p-8 relative overflow-hidden"
          >
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="70%" outerRadius="100%" data={chart} startAngle={220} endAngle={-40}>
                  <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                  <RadialBar background={{ fill: "#F3F4F6" }} dataKey="value" cornerRadius={100} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="font-serif-display text-6xl text-[#1E1B4B]">{data.overall}</div>
                  <div className="overline mt-1">{data.level}</div>
                </div>
              </div>
            </div>
            <p className="mt-6 text-stone-700 text-sm leading-relaxed">{data.message}</p>
          </motion.div>

          <div className="space-y-4">
            <div className="border border-stone-200 rounded-2xl bg-white p-6">
              <div className="overline">Your Strengths</div>
              <ul className="mt-3 space-y-2">
                {data.strengths.map((s) => (
                  <li key={s} className="flex items-center justify-between border-b border-stone-100 pb-2">
                    <span className="text-sm text-[#1E1B4B]">{s}</span>
                    <span className="font-serif-display text-amber-700">{data.dimensions[s]}%</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-stone-200 rounded-2xl bg-white p-6">
              <div className="overline">Growth Areas</div>
              <ul className="mt-3 space-y-2">
                {data.growth_areas.map((s) => (
                  <li key={s} className="flex items-center justify-between border-b border-stone-100 pb-2">
                    <span className="text-sm text-[#1E1B4B]">{s}</span>
                    <span className="font-serif-display text-stone-500">{data.dimensions[s]}%</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl p-6" style={{ background: "#1E1B4B", color: "white" }}>
              <div className="overline" style={{ color: "#FCD34D" }}>A quiet suggestion</div>
              <p className="text-sm mt-2 text-stone-300">Journey (Elite) tier includes a monthly session with a certified relationship counsellor — the cheapest way to raise this score is honest conversation with a professional.</p>
              <Link to="/subscription" data-testid="readiness-upgrade" className="btn-pill inline-flex mt-4 bg-amber-500 text-[#1E1B4B] text-sm">See Journey plan</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
